/**
 * Orders table component with pagination.
 */

import { formatDate } from '@/utils/helpers';
import { useTranslation } from 'react-i18next';
import { Badge, Pagination, Table } from 'react-bootstrap';

interface Order {
  id: number;
  order_number: string;
  status: string;
  created_at: string;
  phone_verified: boolean;
  error_message?: string;
}

interface OrdersTableProps {
  data: any;
  onOrderClick: (orderId: number, action: 'details' | 'download') => void;
  onPageChange: (page: number) => void;
}

const getStatusBadge = (status: string) => {
  const variants: Record<string, string> = {
    COMPLETED: 'success',
    PENDING: 'warning',
    IN_PROGRESS: 'info',
    FAILED: 'danger',
    INITIATED: 'secondary',
    SUBMITTED: 'secondary',
    CANCELLED: 'secondary',
    EXPIRED: 'secondary',
  };
  return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
};

const getNextAction = (order: Order, t: (key: string) => string): string => {
  if (order.status === 'PENDING' && !order.phone_verified) {
    return t('orders.verifyPhone');
  }
  if (order.status === 'COMPLETED') {
    return t('common.download');
  }
  if (['INITIATED', 'SUBMITTED', 'IN_PROGRESS'].includes(order.status)) {
    return t('orders.processing');
  }
  if (order.status === 'FAILED') {
    return order.error_message ? `${t('orders.failed')}: ${order.error_message}` : t('orders.failedContactSupport');
  }
  return '';
};

export default function OrdersTable({ data, onOrderClick, onPageChange }: OrdersTableProps) {
  const { t } = useTranslation();

  if (!data || (!data.items || (Array.isArray(data) && data.length === 0))) {
    const total = data?.total || 0;
    return (
      <div>
        <p>{t('orders.noOrdersFound')}</p>
        {total > 0 && <small className="text-muted">{t('common.total')}: {total} {t('common.orders').toLowerCase()}</small>}
      </div>
    );
  }

  const orders = (data.items || data) as Order[];
  const { page = 1, pages = 1, total = orders.length, per_page = 20 } = data;

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pages) {
      onPageChange(newPage);
    }
  };

  return (
    <div>
      <p className="mb-3" style={{ color: 'var(--myant-text-secondary)', fontWeight: 500 }}>
        {t('orders.showing', { count: orders.length, total, page, pages, perPage: per_page })}
      </p>
      <div style={{ borderRadius: 'var(--myant-radius-lg)', overflow: 'hidden', border: '1px solid var(--myant-border-light)' }}>
        <Table striped hover responsive style={{ marginBottom: 0 }}>
          <thead>
            <tr>
              <th style={{ fontWeight: 600 }}>{t('common.id')}</th>
              <th style={{ fontWeight: 600 }}>{t('orders.orderNumber')}</th>
              <th style={{ fontWeight: 600 }}>{t('common.status')}</th>
              <th style={{ fontWeight: 600 }}>{t('common.created')}</th>
              <th style={{ fontWeight: 600 }}>{t('common.details')}</th>
              <th style={{ fontWeight: 600 }}>{t('orders.nextAction')}</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => {
              const nextAction = getNextAction(order, t);
              return (
                <tr key={order.id} style={{ transition: 'background-color 0.2s ease-in-out' }}>
                  <td>{order.id}</td>
                  <td>{order.order_number}</td>
                  <td>{getStatusBadge(order.status)}</td>
                  <td>{formatDate(order.created_at)}</td>
                  <td>
                    <button
                      className="btn btn-link p-0"
                      onClick={() => onOrderClick(order.id, 'details')}
                      style={{
                        textDecoration: 'none',
                        color: 'var(--myant-primary)',
                        fontWeight: 500,
                        border: 'none',
                        background: 'none',
                        padding: 0
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.textDecoration = 'underline';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.textDecoration = 'none';
                      }}
                    >
                      {t('orders.viewDetails')}
                    </button>
                  </td>
                  <td>
                    {nextAction === t('common.download') ? (
                      <button
                        className="btn btn-link p-0"
                        onClick={() => onOrderClick(order.id, 'download')}
                        style={{
                          textDecoration: 'none',
                          color: 'var(--myant-primary)',
                          fontWeight: 600,
                          border: 'none',
                          background: 'none',
                          padding: 0
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.textDecoration = 'underline';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.textDecoration = 'none';
                        }}
                      >
                        {nextAction}
                      </button>
                    ) : nextAction === t('orders.verifyPhone') ? (
                      <span style={{
                        color: 'var(--myant-primary)',
                        textDecoration: 'none',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.textDecoration = 'underline';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.textDecoration = 'none';
                        }}
                      >
                        {nextAction}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--myant-text-secondary)' }}>{nextAction}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
      <div className="mt-3 d-flex justify-content-between align-items-center">
        <small style={{ color: 'var(--myant-text-secondary)' }}>
          {t('orders.pageOf', { page, pages, total })}
        </small>
        {pages > 1 && (
          <Pagination className="mb-0">
            <Pagination.First onClick={() => handlePageChange(1)} disabled={page === 1} />
            <Pagination.Prev onClick={() => handlePageChange(page - 1)} disabled={page === 1} />
            {Array.from({ length: Math.min(5, pages) }, (_, i) => {
              let pageNum;
              if (pages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= pages - 2) {
                pageNum = pages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }
              return (
                <Pagination.Item
                  key={pageNum}
                  active={pageNum === page}
                  onClick={() => handlePageChange(pageNum)}
                >
                  {pageNum}
                </Pagination.Item>
              );
            })}
            <Pagination.Next onClick={() => handlePageChange(page + 1)} disabled={page === pages} />
            <Pagination.Last onClick={() => handlePageChange(pages)} disabled={page === pages} />
          </Pagination>
        )}
      </div>
    </div>
  );
}
